import {
  START_POST_FORM_ROUTINE,
  CLOSE_POST_FORM,
  UPDATE_POST_FORM_FIELD,
  SAVE_POST_FORM_ROUTINE,
  SELECT_POST_FORM_PLATFORM,
  TOGGLE_POST_FORM_PLATFORM_LINK,
  UPDATE_POST_FORM_VALIDATION,
  CHECK_INSTAGRAM_POST_OVERLAPPING,
  CLOSE_ALL_POST_FORMS,
} from "constants/ActionTypes";
import { createReducer, current, isDraft } from "@reduxjs/toolkit";
import type { Draft } from "@reduxjs/toolkit";
import { extractCorePost, getPostState, detectPostType } from "utils/posts";
import { pickThumbnailFallback } from "utils/media";
import { getPlatforms } from "utils/accounts";
import { FIELDS, PostFormFieldName } from "constants/PostFormFields";
import { POST_TYPES, PostStatesType } from "constants/PostsView";
import {
  VIDEO_COVER_TIMESTAMPS,
  VIDEO_COVER_TYPES,
} from "constants/VideoCoverConstants";
import { postPlatformsConfig } from "components/Posts/PostForm/postPlatforms.config";
import { validate } from "components/Posts/PostForm/postFormValidator";
import remove from "lodash/remove";
import uniq from "lodash/uniq";
import set from "lodash/set";
import unset from "lodash/unset";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import {
  FormPlatformType,
  FormValidationErrors,
  FormValidationWarnings,
  LoadedDraft,
  PlatformFields,
  Post,
  PostFormState,
  PostMediaFile,
} from "../../shared/types/post";
import { AccountType } from "../../shared/types/accounts";
import { PlatformSpec } from "../../constants/Platforms";
import { IOverlapSchedule } from "../../components/Posts/PostForm/types";
import {
  createUnlinkedOverride,
  seedOverrideParentForPath,
  sparsifyPlatformFields,
} from "../../components/Posts/PostForm/platform-fields/sparse-overrides";
import { OrderedRecord } from "../../types/redux-store";
import {
  setCoverPreviewUrl,
  setIsFormDirty,
  setSaveData,
} from "../../features/post/bulk-create/bulk-create-post.actions";
import { FormSaveData } from "../../features/post/bulk-create/bulk-create-post.types";
import { StartPostFormSuccessPayload } from "../types";

function getInitalView() {
  return {
    selectedPlatform: "LINKED" as FormPlatformType,
    visibleFields: [],
  };
}

const VIDEO_COVER_FIELDS = [
  FIELDS.VIDEO_COVER_PREVIEW,
  FIELDS.VIDEO_COVER_ORIGINAL,
  FIELDS.VIDEO_COVER_ORIGINAL_TIMESTAMP,
  FIELDS.VIDEO_COVER_ORIGINAL_TYPE,
  FIELDS.VIDEO_COVER_PREVIEW_SOURCE,
  FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP,
  FIELDS.VIDEO_COVER_TYPE,
];

// A previously chosen video cover must not survive removing the video or
// swapping it for a different asset; the new video should fall back to its
// default cover. Covers can live on the shared post or, for unlinked cluster
// posts, on a per-platform override, so both are cleared.
function resetVideoCoverFields(target: object | null | undefined) {
  if (!target) return;
  VIDEO_COVER_FIELDS.forEach((field) => unset(target, field));
}

type PrimaryMediaSource = {
  mediaFiles?: Pick<PostMediaFile, "url" | "originalUrl" | "origUrl">[];
  url?: string | null;
  originalUrl?: string | null;
  origUrl?: string | null;
};

type PostWithLegacyId = {
  id?: string;
};

function getPrimaryMediaIdentity(post: PrimaryMediaSource | undefined) {
  const primaryMediaFile = post?.mediaFiles?.[0];
  const url = primaryMediaFile?.url ?? post?.url;
  const originalUrl =
    primaryMediaFile?.originalUrl ??
    primaryMediaFile?.origUrl ??
    post?.originalUrl ??
    post?.origUrl ??
    url;

  if (!url && !originalUrl) {
    return null;
  }

  return { url, originalUrl };
}

function plainValue<T>(value: T): T {
  return isDraft(value) ? (current(value as Draft<T>) as T) : value;
}

function validatePostForm(form: PostFormState) {
  const [errors, warnings] = validate(
    form.postState,
    plainValue(form.post),
    plainValue(form.platformFields),
    plainValue(form.platforms),
    plainValue(form.overlapSchedule),
    undefined,
    form.userTimezone ?? ""
  );

  form.validation = {
    errors,
    warnings,
  };
}

const initialFormState = {
  overlapSchedule: {
    accountName: null,
    overlappingPostId: null,
    nextRunAt: null,
  } as IOverlapSchedule,
  originalPost: {} as Post,
  originalPostState: "",
  postState: "",
  post: {},
  platformFields: {} as PlatformFields,
  accounts: [] as AccountType[],
  platforms: [] as PlatformSpec[],
  view: getInitalView(),
  validation: {
    errors: {},
    warnings: {},
  },
  results: null,
} as const;

const initialState = {
  byId: {},
  allIds: [],
} as OrderedRecord<PostFormState>;

type PostFormsState = typeof initialState;
let formRequestedCount = 1;
const postFormsReducer = createReducer(initialState, (builder) => {
  builder.addCase(
    START_POST_FORM_ROUTINE.SUCCESS,
    (
      state,
      action: {
        payload: StartPostFormSuccessPayload;
      }
    ) => {
      const {
        post,
        accounts,
        requestType,
        accountSelectionDisabled,
        form,
        requestFormTitle,
        userTimezone,
      } = action.payload;
      const denormalizedPost = extractCorePost(post);
      const postId = post._id || (post as PostWithLegacyId).id;
      const formId = postId || `requested-${formRequestedCount}`;
      const normalizedRequestedPost =
        denormalizedPost._id || !postId
          ? denormalizedPost
          : { ...denormalizedPost, _id: postId };
      const platforms = getPlatforms(accounts);
      const originalPostState = getPostState(post);
      const visibleFields = getVisibleFields("LINKED", platforms);

      // if (originalPostState === "ARCHIVED") {
      //   // this avoids a 'date in the past' error when you are
      //   // "posting again" from archives page
      //   post.when = null;
      // }
      const newForm: PostFormState = {
        ...initialFormState,
        originalPost: normalizedRequestedPost,
        originalPostState: originalPostState,
        post: buildInitialPost(post, accounts),
        postState: originalPostState,
        accounts: accounts,
        platforms: platforms,
        view: {
          ...initialFormState.view,
          visibleFields: visibleFields,
        },
        requestType,
        requestedPost: normalizedRequestedPost,
        requestedId: formRequestedCount++,
        isDirty: requestType === "create",
        accountSelectionDisabled,
        requestFormTitle: requestFormTitle,
        userTimezone,
        ...form,
      };

      // Migrate any legacy FULL-SNAPSHOT per-platform overrides (arriving via
      // `...form` from restored localStorage drafts or duplicated posts) to the
      // sparse model, so future shared-post edits inherit for un-customised
      // fields. Behaviour-preserving: only drops keys deep-equal to base, which
      // are inert under the read merge. Inert for fresh forms (platformFields
      // starts empty).
      newForm.platformFields = sparsifyPlatformFields(
        newForm.platformFields,
        newForm.post
      );

      validatePostForm(newForm);

      state.byId[formId] = newForm;
      if (!state.allIds.includes(formId)) {
        state.allIds.push(formId);
      }
      console.log("call START_POST_FORM_ROUTINE.SUCCESS");
      return state;
    }
  );
  builder.addCase(
    CLOSE_POST_FORM,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state, action: any) => {
      const { postId } = action.payload as { postId: string };
      if (postId && state.allIds.includes(postId)) {
        remove(state.allIds, (selectedId) => selectedId === postId);
        delete state.byId[postId];
      }
      return state;
    }
  );
  builder.addCase(CLOSE_ALL_POST_FORMS, (state) => {
    return {
      ...state,
      allIds: [],
      byId: {},
    };
  });
  builder.addCase(
    UPDATE_POST_FORM_VALIDATION,
    (
      state,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      action: any
    ) => {
      const { postId, errors, warnings } = action.payload as {
        postId: string;
        errors: FormValidationErrors;
        warnings: FormValidationWarnings;
      };
      state.byId[postId].validation = { errors, warnings };
      return state;
    }
  );
  builder.addCase(
    SAVE_POST_FORM_ROUTINE.SUCCESS,
    (
      state,
      action: {
        payload: {
          postId: string;
          originalPost: Post;
          newPosts: Post[];
        };
      }
    ) => {
      const { postId, originalPost, newPosts } = action.payload;
      const post = state.byId[postId];
      if (post) {
        post.results = { originalPost, newPosts };
      }
      return state;
    }
  );
  builder.addCase(
    SAVE_POST_FORM_ROUTINE.FAILURE,
    (
      state,
      action: {
        payload: string | { message: string; postId?: string };
      }
    ) => {
      type WrapMessage = { message: string; postId?: string };
      const { payload } = action;
      const form = getByIdOrFirst(state, (payload as WrapMessage).postId);
      const id = form.post._id;
      const errorMessage = (payload as WrapMessage).message
        ? (payload as WrapMessage).message
        : payload;
      if (errorMessage) {
        form.validation.errors["SAVE_ERROR"] = {
          errors: [errorMessage],
        };
        state.byId[id] = form;
      }
      return state;
    }
  );
  builder.addCase(
    SELECT_POST_FORM_PLATFORM,
    (
      state,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      action: any
    ) => {
      const { platformType, postId } = action.payload as {
        platformType: FormPlatformType | null;
        postId: string;
      };
      const form = getByIdOrFirst(state, postId);
      const id = form.post._id;

      const platforms = form.platforms;
      if (platformType !== null) {
        let fields: string[] = [];
        if (platformType === "LINKED") {
          platforms.map((p) => {
            // eslint-disable-next-line unicorn/prefer-spread
            fields = fields.concat(postPlatformsConfig[p.TYPE].fields);
          });
        } else if (postPlatformsConfig[platformType]) {
          fields = [...postPlatformsConfig[platformType].fields];
        }
        fields = uniq(fields);
        form.view.visibleFields = fields;
        form.view.selectedPlatform = platformType;
      }
      state.byId[id] = form;
      return state;
    }
  );
  builder.addCase(
    TOGGLE_POST_FORM_PLATFORM_LINK,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state, action: any) => {
      const { payload } = (action || {}) as {
        payload?: { postId: string } | string;
      };
      const form = getByIdOrFirst(
        state,
        typeof payload === "object" ? payload.postId : undefined
      );
      const id = form.post._id;

      const currentFields = form.platformFields[form.view.selectedPlatform];
      const newPlatformFields = { ...form.platformFields };
      if (currentFields && currentFields.unlinked) {
        delete newPlatformFields[form.view.selectedPlatform];
      } else {
        // Sparse override: start empty (just the `unlinked` marker) so every
        // field inherits the current shared post until the user edits it on
        // this platform's own tab. Avoids the frozen-snapshot data loss where
        // later base edits were masked or an empty snapshot published empty.
        newPlatformFields[form.view.selectedPlatform] =
          createUnlinkedOverride();
      }
      form.platformFields = newPlatformFields;
      state.byId[id] = form;
      return state;
    }
  );
  builder.addCase(
    UPDATE_POST_FORM_FIELD,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state, action: any) => {
      const { postId, values } = action.payload as {
        postId: string;
        values: Record<PostFormFieldName, unknown>;
      };
      const fields = Object.keys(values) as PostFormFieldName[];

      if (!fields || fields.length === 0) {
        console.log("Invalid call to updateField");
        return state;
      }

      const form = state.byId[postId];

      if (!form) return state;
      // Mutate the drafts. Unwrapping to a plain snapshot and spreading it only
      // makes the top level writable - nested objects stay frozen, and
      // `set(formPost, "gmbEvent.startDate", ...)` silently no-ops against them.
      // Immer copies only the touched paths, so this stays cheap on large posts.
      const formPost = form.post;
      const newPlatformFields = form.platformFields;
      // detectPostType cannot express every stored type (fb-reel, photo), so
      // only recompute when fields that actually affect derived type change.
      let typeNeedsUpdate = false;

      for (const fieldString of fields) {
        const fieldParts = fieldString.split(".");
        const field = fieldParts.at(-1) as string;
        const value = values[fieldString];

        switch (field) {
          case FIELDS.POST_STATE: {
            form.postState = value as PostStatesType;
            continue;
          }
          case FIELDS.ACCOUNTS: {
            form.platforms = getPlatforms(value as AccountType[]);
            form.accounts = value as AccountType[];
            formPost.accounts = value as AccountType[];
            formPost.accountIds = map(value as AccountType[], "_id");
            if (formPost.accounts.length === 0) {
              // if there are not selected account reset value to initial
              form.view = getInitalView();
            }
            continue;
          }
          case FIELDS.MEDIA: {
            const mediaFiles = (value as PostMediaFile[]) || [];

            const previousPrimary = getPrimaryMediaIdentity(formPost);
            const nextPrimary = getPrimaryMediaIdentity({
              mediaFiles,
            });
            const primaryAssetChanged =
              !!previousPrimary &&
              (previousPrimary.url !== nextPrimary?.url ||
                previousPrimary.originalUrl !== nextPrimary?.originalUrl);
            const shouldPreserveSelectedCoverThumbnail =
              Boolean(previousPrimary) &&
              !primaryAssetChanged &&
              (formPost.videoCoverType === VIDEO_COVER_TYPES.UPLOADED ||
                formPost.videoCoverType === VIDEO_COVER_TYPES.FRAME_SELECTED ||
                formPost.videoCoverImageTimestamp ===
                  VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER ||
                (typeof formPost.videoCoverImageTimestamp === "number" &&
                  formPost.videoCoverImageTimestamp >
                    VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME));
            if (mediaFiles.length === 0 || primaryAssetChanged) {
              resetVideoCoverFields(formPost);
              (Object.keys(newPlatformFields) as FormPlatformType[]).forEach(
                (platform) => resetVideoCoverFields(newPlatformFields[platform])
              );
            }

            formPost.mediaFiles = mediaFiles;
            formPost.media = null; // deprecated, use mediaFiles instead

            // Remove media file fields so that preview knows it's a text post;
            if (mediaFiles.length === 0) {
              formPost.fileName = null;
              formPost.isVideo = false;
              formPost.origUrl = null;
              formPost.thumbnailUrl = null;
              formPost.url = null;
              formPost.videoPostType = null;
            }

            if (mediaFiles.length > 0) {
              // Need to set the top level of the post since it's used all over
              formPost["altText"] = mediaFiles[0].altText;
              formPost["url"] = mediaFiles[0].url;
              formPost["meta"] = mediaFiles[0].meta;
              formPost["fileName"] = mediaFiles[0].fileName;
              if (!shouldPreserveSelectedCoverThumbnail) {
                formPost["thumbnailUrl"] =
                  pickThumbnailFallback(mediaFiles[0]) ?? "";
              }
              formPost["isVideo"] = mediaFiles[0].isVideo;

              if (!formPost["origUrl"]) {
                formPost["origUrl"] = mediaFiles[0].originalUrl;
                formPost["originalUrl"] = mediaFiles[0].originalUrl;
              }

              if (!formPost["originalUrl"]) {
                formPost["origUrl"] = mediaFiles[0].originalUrl;
                formPost["originalUrl"] = mediaFiles[0].originalUrl;
              }

              // Single asset posts don't have a media files unfortunately.
              if (mediaFiles.length === 1) {
                formPost["altText"] = mediaFiles[0].altText;
                formPost["origUrl"] = mediaFiles[0].originalUrl;
                formPost["originalUrl"] = mediaFiles[0].originalUrl;
              }
            }

            typeNeedsUpdate = true;
            continue;
          }
          // No default
        }

        // fields that should be applied to all platforms, not only selected one
        const allPlatformsFields = [
          FIELDS.WHEN,
          FIELDS.IS_NOW,
          FIELDS.POST_STATUS,
        ] as string[];

        const targetPlatform = (action.payload.targetPlatform ??
          form.view.selectedPlatform) as FormPlatformType;
        const isUnlinkedClusterPost =
          targetPlatform !== "LINKED" &&
          form.platforms.length > 1 &&
          newPlatformFields[targetPlatform]?.unlinked === true;
        const isAllPlatformField = allPlatformsFields.includes(field);
        // Cluster post
        if (isUnlinkedClusterPost && !isAllPlatformField) {
          const selectedPlatform = targetPlatform;
          const override =
            newPlatformFields[selectedPlatform] ?? createUnlinkedOverride();
          override.unlinked = true;
          newPlatformFields[selectedPlatform] = override;

          // For nested paths (e.g. gmbEvent.title), seed the whole parent
          // object from base first so a sparse override never holds a partial
          // parent that the read-merge would use to drop base's sibling
          // sub-fields.
          seedOverrideParentForPath(
            override,
            plainValue(formPost),
            fieldString
          );

          set(override, fieldString, value);
        } else {
          set(formPost, fieldString, value);
        }

        // Need to set fields that may not have been set prior
        if (field === FIELDS.IS_STORY && value === false) {
          formPost[FIELDS.CAPTION] = formPost[FIELDS.CAPTION] || "";
          formPost[FIELDS.FIRST_COMMENT] = formPost[FIELDS.FIRST_COMMENT] || "";
          formPost[FIELDS.USER_TAGS] = formPost[FIELDS.USER_TAGS] || [];
          formPost[FIELDS.PRODUCT_TAGS] = formPost[FIELDS.PRODUCT_TAGS] || [];
        }

        // May need to do a type update (i.e. story toggle).
        if (field === FIELDS.IS_STORY) {
          typeNeedsUpdate = true;
        }
      }

      // Derived purely from the final media/story state, so once per dispatch
      // is enough. The old code recomputed it on every field.
      if (typeNeedsUpdate) {
        formPost["type"] = detectPostType(formPost);
      }

      validatePostForm(form);

      state.byId[postId] = form;
      return state;
    }
  );
  builder.addCase(
    CHECK_INSTAGRAM_POST_OVERLAPPING.SUCCESS,
    (state, { payload: { postId, data } }) => {
      state.byId[postId].overlapSchedule = data;

      const form = state.byId[postId];

      validatePostForm(form);

      state.byId[postId] = form;

      return state;
    }
  );
  builder.addCase(CHECK_INSTAGRAM_POST_OVERLAPPING.FAILURE, (state, action) => {
    const { meta } = action;
    const postId = meta?.postId;
    if (postId && state.byId[postId]) {
      const form = state.byId[postId];
      form.validation.errors["OVERLAP_ERROR"] = {
        errors: [
          "Unable to verify Instagram posting time. Please try again or choose a different time.",
        ],
      };
      state.byId[postId] = form;
    }
    return state;
  });
  builder.addCase(
    setSaveData.type,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state, action: any) => {
      const { id, data } = action.payload as { id: string; data: FormSaveData };
      return updateStateById(state, id, { formSaveData: data, isDirty: false });
    }
  );
  builder.addCase(
    setIsFormDirty.type,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state, action: any) => {
      const { id, isDirty } = action.payload as {
        id: string;
        isDirty: boolean;
      };
      return updateStateById(state, id, { isDirty });
    }
  );
  builder.addCase(
    setCoverPreviewUrl.type,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state, action: any) => {
      const { id, url } = action.payload as { id: string; url: string };
      return updateStateById(state, id, { coverPreviewUrl: url });
    }
  );
});

function updateStateById(
  state: PostFormsState,
  id: string,
  newState: Partial<PostFormState>
): PostFormsState {
  if (id && state.byId[id]) {
    const form = state.byId[id];
    return {
      ...state,
      byId: {
        ...state.byId,
        [id]: { ...form, ...newState },
      },
    };
  }
  return state;
}

const buildInitialPost = (
  post: Post | LoadedDraft,
  accounts: AccountType[]
) => {
  const normalizedPost = extractCorePost(post);
  const accountIds = map(accounts, "_id");

  const postCopy = {
    piBoards: [],
    ...normalizedPost,
    isStory: normalizedPost.type === POST_TYPES.STORY,
    accounts: accounts,
    accountIds: accountIds,
  };

  // because with drafts, post.schedule will not contain the ID
  if (!postCopy._id) {
    const postId = post._id || (post as PostWithLegacyId).id;
    if (postId) {
      postCopy._id = postId;
    }
  }

  delete postCopy.postState;

  if (
    normalizedPost.when &&
    typeof normalizedPost.when === "string" &&
    normalizedPost.when.toLowerCase() === "invalid date"
  ) {
    postCopy.when = null;
  }

  // We use both of these but the previewer relies on originalUrl
  if (postCopy.origUrl && !postCopy.originalUrl) {
    postCopy["originalUrl"] = postCopy.origUrl;
  }

  // Some story posts don't set original url
  if (!postCopy.origUrl && !postCopy.originalUrl && postCopy.url) {
    postCopy["originalUrl"] = postCopy.url;
    postCopy["origUrl"] = postCopy.url;
  }

  if (
    isEmpty(postCopy.mediaFiles) && // There is a bug where a story that has a single video doesn't set isVideo
    postCopy.isStory &&
    postCopy.originalUrl?.includes(".mp4")
  ) {
    postCopy.isVideo = true;
  }

  // Populate videoCoverPreview from thumbnailUrl when loading a draft with a custom cover
  // videoCoverPreview is not persisted to the API, but thumbnailUrl is
  if (
    !postCopy.videoCoverPreview &&
    postCopy.thumbnailUrl &&
    postCopy.videoCoverImageTimestamp !== undefined &&
    postCopy.videoCoverImageTimestamp !== 0
  ) {
    postCopy.videoCoverPreview = postCopy.thumbnailUrl;
  }

  return postCopy;
};

const getVisibleFields = (
  selectedPlatform: FormPlatformType,
  platforms: PlatformSpec[]
) => {
  if (selectedPlatform === null) {
    return [];
  }
  let fields: string[] = [];
  if (selectedPlatform === "LINKED") {
    platforms.map((platform) => {
      const platformFields = postPlatformsConfig[platform.TYPE];
      if (platformFields) {
        // eslint-disable-next-line unicorn/prefer-spread
        fields = fields.concat(platformFields.fields);
      }
    });
  } else {
    fields = [...postPlatformsConfig[selectedPlatform].fields];
  }
  fields = uniq(fields);
  return fields;
};

const getByIdOrFirst = (state: PostFormsState, formId: string | undefined) => {
  const id = formId || state.allIds[0];
  return state.byId[id];
};

export default postFormsReducer;
